import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Flex, useToast, Heading, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import { ListingAttributes, ListingsView } from 'src/components/organisms';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { AttributeSelectorProvider } from 'src/contexts/AttributeSelector';
import { Attribute, ThirdPartyQuery } from 'src/api/v1-api';
import { SortOption } from 'src/constants/sort-options';
import useRequest from 'src/utils/hooks/useRequest';
import { useAuth } from 'src/utils/auth';
import { useRouter } from 'next/router';
import SingleListingImportsTable from 'src/components/organisms/single-listing-imports-table';
import { CoverLoader } from 'src/components/atoms';
import { FilterableCategoryCards } from 'src/components/molecules';

const ListingApprovals = (): JSX.Element => {
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [queries, setQueries] = useState<ThirdPartyQuery[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [aiSortAvailable, setAiSortAvailable] = useState(false);

  const { marketplace, categories, isAffiliate } = useContext(MarketplaceContext);
  const { api } = useRequest();
  const toast = useToast();
  const { userLoaded, hasStaff } = useAuth();
  const isLowTier =
    !marketplace ||
    !marketplace.tier ||
    marketplace.tier.position === 1 ||
    marketplace.tier.position === 2;
  const isStaffUser = hasStaff();
  const showSearchQueries = isStaffUser || (isAffiliate === false && !isLowTier);
  const router = useRouter();
  const isAdminRoute = router.pathname.startsWith('/slug_admin');

  useEffect(() => {
    if (
      router.isReady &&
      router.query.message &&
      router.query.success === 'true' &&
      !toast.isActive('listing-approval-success')
    ) {
      toast({
        title: 'Success',
        description: router.query.message,
        status: 'success',
        duration: 5000,
        isClosable: true,
        id: 'listing-approval-success',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query]);

  useEffect(() => {
    const fetchQueries = async () => {
      if (!marketplace?.id) return;

      try {
        setIsLoading(true);
        const response = await api.marketplace.query.list(marketplace.id);
        setQueries(response);
      } catch (error) {
        toast({
          title: 'Error fetching Saved Searches',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (userLoaded && marketplace?.id && showSearchQueries) {
      fetchQueries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace?.id, userLoaded, showSearchQueries]);

  const handleResponseMetadata = useCallback((response: any) => {
    if (response?.ai_sort_available !== undefined) {
      setAiSortAvailable(response.ai_sort_available === true);
    }
  }, []);

  useEffect(() => {
    if (!categories || categories.length === 0) return;
    const sourceAttributes = {
      id: 1,
      options: [
        {
          id: 1,
          name: 'Loma',
          value: 'loma',
          position: 0,
        },
        {
          id: 2,
          name: 'Facebook',
          value: 'facebook',
          position: 1,
        },
        {
          id: 3,
          name: 'Craigslist',
          value: 'craigslist',
          position: 2,
        },
        {
          id: 4,
          name: 'eBay',
          value: 'ebay',
          position: 3,
        },
        {
          id: 5,
          name: 'Google',
          value: 'google',
          position: 4,
        },
        {
          id: 6,
          name: 'Yelp',
          value: 'yelp',
          position: 5,
        },
        {
          id: 7,
          name: 'Other',
          value: 'other',
          position: 6,
        },
      ],
      format: 'multiselect',
      required: false,
      name: 'source',
      main_section_display: false,
      min_value: '',
      max_value: '',
      content_type_name: 'Listing',
      buyer_description: '',
      seller_description: '',
      position: 1,
      sensitive: false,
      filter_display: true,
      filter_min_value: '',
      filter_max_value: '',
      collect_in_modal_submission: false,
    };

    const queriesOptions = queries.map((query) => ({
      id: query.id ?? 0,
      name: `${query.third_party_platform} - ${query.search_term}`,
      value: `${query.id ?? 0}`,
      position: query.id ?? 0,
    }));

    const queriesAttributes = {
      id: 2,
      options: queriesOptions,
      format: 'multiselect',
      required: false,
      name: 'queries',
      main_section_display: false,
      min_value: '',
      max_value: '',
      content_type_name: 'Listing',
      buyer_description: '',
      seller_description: '',
      position: 2,
      sensitive: false,
      filter_display: true,
      filter_min_value: '',
      filter_max_value: '',
      collect_in_modal_submission: false,
    };

    const categoryAttributes = {
      id: 3,
      options: categories.map((category) => ({
        id: category.id,
        name: category.name,
        value: `${category.id}`,
        position: category.position,
        parentId:
          // @ts-ignore
          typeof category.parent === 'number' ? category?.parent : category?.parent?.id ?? null,
      })),
      format: 'tree',
      required: false,
      name: 'category',
      main_section_display: false,
      min_value: '',
      max_value: '',
      content_type_name: 'Listing',
      buyer_description: '',
      seller_description: '',
      position: 3,
      sensitive: false,
      filter_display: true,
      filter_min_value: '',
      filter_max_value: '',
      collect_in_modal_submission: false,
    };

    showSearchQueries
      ? setAttributes([sourceAttributes, queriesAttributes, categoryAttributes])
      : setAttributes([categoryAttributes]);
  }, [queries, categories, showSearchQueries]);

  const [activeTab, setActiveTab] = useState(0);

  return (
    <Flex w="full" direction="column" rowGap={{ base: 6, md: 7, lg: 8 }}>
      {!isAdminRoute && (
        <Heading fontSize="2xl" data-test="page-title">
          Listing Approvals
        </Heading>
      )}
      <Tabs variant="enclosed" mt={2} index={activeTab} onChange={setActiveTab} overflow="visible">
        <TabList>
          {!isAdminRoute && (
            <>
              <Tab>Pending Listings</Tab>
              <Tab>Recent Imports</Tab>
            </>
          )}
        </TabList>
        <TabPanels overflow="visible">
          <TabPanel px={0} overflow="visible">
            {isLoading ? (
              <CoverLoader context="ListingApprovals:LoadingFilters" />
            ) : (
              <AttributeSelectorProvider
                attributes={attributes}
                useToggle={false}
                defaultViewType="grid"
                sortOptions={((): SortOption[] => {
                  const baseOptions: SortOption[] = isAdminRoute
                    ? aiSortAvailable
                      ? ['ai-sort', 'newest', 'oldest']
                      : ['newest', 'oldest']
                    : aiSortAvailable
                    ? ['source-loma', 'ai-sort', 'newest', 'oldest']
                    : ['source-loma', 'newest', 'oldest'];
                  return baseOptions;
                })()}
                maxPrice={0}
              >
                {isAdminRoute && categories.length > 0 && (
                  <FilterableCategoryCards categories={categories} />
                )}
                <ListingAttributes hideFilters={isAdminRoute} />
                <ListingsView
                  fetchUrl={`${process.env.NEXT_PUBLIC_API_HOST}/pending_listings/${marketplace?.id}`}
                  requiresAuth={true}
                  inventoryDisplay={'4_2_grid'}
                  paginationStyle={''}
                  resultsPerPage={38}
                  actionButtons="pendingListings"
                  propNumDisplayListings={32}
                  onResponseMetadata={handleResponseMetadata}
                />
              </AttributeSelectorProvider>
            )}
          </TabPanel>
          {!isAdminRoute && (
            <TabPanel px={0}>
              <SingleListingImportsTable isActive={activeTab === 1} />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Flex>
  );
};

export default ListingApprovals;
